import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Send, Bot, User, RefreshCw, MessageSquarePlus, Terminal, Power, PowerOff, Headphones, Check, CheckCheck, FileText, X, Smile, Play, Paperclip, History, Bell, BellOff, PanelRight, PanelRightClose, AlertCircle, Mic, Copy, Zap, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Halo, AC saya bocor",
  "Saya butuh service AC di Jl. Sudirman No. 1, Jakarta Pusat. Besok pagi bisa?",
  "Nama saya Andi, minta cleaning AC 2 unit hari ini",
];

// v45 — Common emoji list untuk picker (curated utk WA chat)
const EMOJI_LIST = [
  "😊","😂","🙏","👍","❤️","👌","🙌","🤝","✅","🔥",
  "😅","😄","😁","🤗","👋","💪","💯","⭐","🎉","🚀",
  "😢","😔","😞","🥲","🤔","😴","😷","🥵","🌡️","💧",
  "❄️","🧊","🌀","☀️","🌧️","⚡","🔧","🔨","⚙️","🛠️",
  "📸","🖼️","🎥","📱","💬","📞","📧","📍","🏠","🏢",
  "🕐","⏰","📅","💰","💵","💳","🧾","🎁","👨‍🔧","👩‍🔧",
];

// v45 — Render media bubble (image thumbnail atau video with play button)
function MediaBubble({ msg, apiBase }) {
  const src = `${apiBase}/api/media/${msg.media_id}`;
  const [enlarged, setEnlarged] = useState(false);
  if (msg.media_type === "image" || msg.media_type === "sticker") {
    return (
      <>
        <button
          onClick={() => setEnlarged(true)}
          className="block max-w-[220px] rounded-md overflow-hidden bg-zinc-100 border border-zinc-200 hover:opacity-90 transition"
          data-testid={`media-thumb-${msg.id}`}
        >
          <img src={src} alt={msg.media_caption || "image"} loading="lazy"
               className="w-full h-auto max-h-[240px] object-cover"
               onError={(e) => { e.currentTarget.style.opacity = 0.4; e.currentTarget.alt = "Media expired / gagal muat"; }} />
        </button>
        {enlarged && (
          <div
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setEnlarged(false)}
            data-testid="media-lightbox"
          >
            <img src={src} alt={msg.media_caption || ""} className="max-w-full max-h-full object-contain rounded" />
          </div>
        )}
      </>
    );
  }
  if (msg.media_type === "video") {
    return (
      <video
        src={src} controls preload="metadata"
        className="max-w-[280px] max-h-[300px] rounded-md bg-black"
        data-testid={`media-video-${msg.id}`}
      />
    );
  }
  if (msg.media_type === "audio") {
    return <audio src={src} controls className="max-w-[240px]" data-testid={`media-audio-${msg.id}`} />;
  }
  // document / sticker fallback → download link
  return (
    <a href={src} target="_blank" rel="noreferrer"
       className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-100 border border-zinc-200 text-[12px] font-semibold hover:bg-zinc-200"
       data-testid={`media-file-${msg.id}`}>
      📎 {msg.media_type || "file"}
    </a>
  );
}

// v40 — Icon tick untuk WA-style status delivery
function StatusTick({ status }) {
  if (!status || status === "pending") {
    // 1 tick abu — belum ke Meta / sent tapi belum wamid — cosmetic pending
    return <Check className="w-3 h-3 text-zinc-400 inline" strokeWidth={2.5} />;
  }
  if (status === "sent") {
    return <Check className="w-3 h-3 text-zinc-400 inline" strokeWidth={2.5} />;
  }
  if (status === "delivered") {
    return <CheckCheck className="w-3 h-3 text-zinc-400 inline" strokeWidth={2.5} />;
  }
  if (status === "read") {
    return <CheckCheck className="w-3 h-3 text-[#53BDEB] inline" strokeWidth={2.5} />;
  }
  if (status === "failed") {
    return <span className="text-[10px] text-red-600 font-bold">!</span>;
  }
  return null;
}

export default function Simulator() {
  const [convos, setConvos] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [currentConv, setCurrentConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  // v83.8 — Progressive load: window-based pagination
  // v84.18 — Load ALL messages of current conversation (no time window).
  // Filter 2 hari sekarang di sidebar (via /api/conversations?days=2).
  // v84.66 — Sidebar pagination: state `sidebarDays` bertambah +2 per klik "Load more".
  // v84.77 — Selalu init ke 2 hari (tidak baca localStorage lagi) — user request:
  //   setiap buka WhatsApp harus default 2 hari, bukan meng-persist range yg pernah dibuka.
  // v84.86 — Chat history: initial load 7 hari (bukan lagi 0 = all). Scroll ke atas
  //   di area chat auto-load 7 hari sebelumnya (batch berikutnya) via handleChatScroll →
  //   loadOlder(). Backend `/conversations/{cid}/messages?days=7&before=<iso>` sudah
  //   support pagination window. Merge auto-refresh preserve msg lebih lama.
  const CHAT_DAYS = 7;
  const [sidebarDays, setSidebarDays] = useState(2);
  const [loadingMoreDays, setLoadingMoreDays] = useState(false);
  const sidebarScrollRef = useRef(null);   // v84.77 — utk deteksi scroll ke atas → reset ke 2 hari
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const oldestTsRef = useRef(null);        // deprecated; kept utk avoid ref-init bug
  const chatScrollRef = useRef(null);      // container scrollable
  const prependingRef = useRef(false);     // skip auto-scroll-to-bottom saat prepend
  const [lastWoAddress, setLastWoAddress] = useState(null);  // v52 — alamat WO terakhir customer di header
  const [recentWos, setRecentWos] = useState([]);            // v53 — legacy fallback
  const [customerHistory, setCustomerHistory] = useState(null); // v74 — full history from customer_history collection
  const [showWoHistory, setShowWoHistory] = useState(false); // v53 — toggle popover
  // v54 — Panel AI Reasoning collapse + sound notif utk pesan WA masuk
  const [showReasoning, setShowReasoning] = useState(() => {
    try { return localStorage.getItem("gac.showReasoning") !== "0"; } catch { return true; }
  });
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("gac.soundOn") !== "0"; } catch { return true; }
  });
  const lastInboundIdsRef = useRef(new Set());   // track inbound message IDs yg sudah dilihat
  const soundAckedRef = useRef(false);            // first paint jangan bunyi
  // v60 — Pencarian kontak: nama, phone, atau kata kunci di chat
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMatchedPhones, setSearchMatchedPhones] = useState(null);  // null = no filter
  // v84.67 — Filter status chips: "all" | "needs_admin" | "scheduled" | "done" | "open" | "24h"
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return localStorage.getItem("gac.statusFilter") || "all"; }
    catch { return "all"; }
  });
  const [reasoning, setReasoning] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [customerName, setCustomerName] = useState("Andi");
  const [customerPhone, setCustomerPhone] = useState("+6281200000001");
  const [aiMode, setAiMode] = useState("manual");    // v44 — "manual" | "semi" | "away"
  const [aiEnabled, setAiEnabled] = useState(false);   // v43 — default OFF (derived from mode)
  // v45 — Emoji picker
  const [showEmoji, setShowEmoji] = useState(false);
  // v84.142 — Attach menu popup + contact modal
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", phone: "" });
  const [sendingContact, setSendingContact] = useState(false);

  // v84.81 — Quick Reply state
  const [showQuickReply, setShowQuickReply] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [newQrText, setNewQrText] = useState("");
  const [qrSaving, setQrSaving] = useState(false);
  async function loadQuickReplies() {
    try {
      const d = await api.listQuickReplies();
      setQuickReplies(d.items || []);
    } catch (e) { /* silent */ }
  }
  async function handlePickQuickReply(qr) {
    setText(t => (t ? t + " " : "") + qr.text);
    setShowQuickReply(false);
    // Increment use count di background (non-blocking)
    api.useQuickReply(qr.id).then(() => loadQuickReplies()).catch(() => {});
  }
  async function handleSaveQuickReply() {
    const t = (newQrText || "").trim();
    if (!t) return;
    setQrSaving(true);
    try {
      await api.createQuickReply(t);
      setNewQrText("");
      await loadQuickReplies();
      toast.success("Quick reply tersimpan");
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    } finally {
      setQrSaving(false);
    }
  }
  async function handleDeleteQuickReply(qr) {
    if (!window.confirm(`Hapus quick reply "${qr.text.slice(0, 40)}"?`)) return;
    try {
      await api.deleteQuickReply(qr.id);
      await loadQuickReplies();
      toast.success("Quick reply dihapus");
    } catch (e) {
      toast.error("Gagal hapus: " + (e?.response?.data?.detail || e.message));
    }
  }
  // v46 — Media upload
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  // v83 — Push-to-talk mic (Web Speech API).
  // Rekam hanya selama tombol ditekan; hasil transcript otomatis di-append
  // ke textarea. Bisa di-edit sebelum kirim.
  const [micRecording, setMicRecording] = useState(false);
  const [micSupported, setMicSupported] = useState(true);
  const recogRef = useRef(null);          // SpeechRecognition instance
  const micActiveRef = useRef(false);     // guard untuk `end` handler
  const micBaseTextRef = useRef("");      // teks awal sebelum record dimulai
  const micFinalRef = useRef("");         // akumulasi transcript FINAL saja (bukan interim)

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setMicSupported(false); return; }
  }, []);

  function startMic() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Browser tidak mendukung Web Speech API"); return; }
    if (micActiveRef.current) return;     // sudah aktif
    try {
      const r = new SR();
      r.lang = "id-ID";                   // Bahasa Indonesia
      r.continuous = true;
      r.interimResults = true;
      micBaseTextRef.current = text;
      micFinalRef.current = "";
      r.onresult = (ev) => {
        let interim = "";
        let finalAdd = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const seg = ev.results[i][0].transcript;
          if (ev.results[i].isFinal) finalAdd += seg;
          else interim += seg;
        }
        if (finalAdd) micFinalRef.current += finalAdd;
        // Tampilkan base + final + interim secara live
        const combined = (micBaseTextRef.current
          + (micBaseTextRef.current && !micBaseTextRef.current.endsWith(" ") ? " " : "")
          + micFinalRef.current + interim).trim();
        setText(combined);
      };
      r.onerror = (ev) => {
        if (ev.error && ev.error !== "no-speech" && ev.error !== "aborted") {
          toast.error("Mic error: " + ev.error);
        }
      };
      r.onend = () => {
        micActiveRef.current = false;
        setMicRecording(false);
      };
      recogRef.current = r;
      micActiveRef.current = true;
      setMicRecording(true);
      r.start();
    } catch (e) {
      toast.error("Gagal mulai mic: " + e.message);
      micActiveRef.current = false;
      setMicRecording(false);
    }
  }

  function stopMic() {
    const r = recogRef.current;
    if (!r || !micActiveRef.current) return;
    try { r.stop(); } catch {}
    // onend akan reset state
  }

  async function handleMediaUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset supaya bisa pilih file yg sama lagi
    if (!file || !currentId) return;
    if (file.size > 16 * 1024 * 1024) {
      toast.error("File terlalu besar (max 16 MB)");
      return;
    }
    setUploading(true);
    try {
      const r = await api.sendMedia(currentId, file, text.trim() || "");
      toast.success(r.simulated ? `${r.meta_type} disimpan (simulasi)` : `${r.meta_type} terkirim ke customer`);
      setText("");
      const d = await api.convoMsgs(currentId, { days: CHAT_DAYS });
      mergeIntoMsgs(d.messages);
    } catch (err) {
      toast.error("Upload gagal: " + (err?.response?.data?.detail || err.message));
    } finally { setUploading(false); }
  }
  const [waMode, setWaMode] = useState("simulator"); // "simulator" | "live"
  const [replyMode, setReplyMode] = useState("admin"); // v43 — default Admin mode saat page load
  // v40 — Template send state
  const [showTemplate, setShowTemplate] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", language: "id", variables: "" });
  const [tplSending, setTplSending] = useState(false);
  const bottomRef = useRef();
  const termRef = useRef();

  async function loadStatus() {
    // v44 — Load current mode (default "manual" if not set). TIDAK force reset,
    // supaya preferensi admin persistent lintas refresh.
    try {
      const s = await api.aiStatus();
      const m = ["manual","semi","away"].includes(s.mode) ? s.mode : "manual";
      setAiMode(m);
      setAiEnabled(m !== "manual");
    } catch {
      setAiMode("manual"); setAiEnabled(false);
    }
    try {
      const wa = await api.waStatus();
      setWaMode(wa?.mode === "live" ? "live" : "simulator");
    } catch {}
  }
  async function setMode(newMode) {
    try {
      await api.aiToggle({ mode: newMode });
      setAiMode(newMode);
      setAiEnabled(newMode !== "manual");
      const label = { manual: "MANUAL", semi: "SEMI AI", away: "AWAY (Full Auto)" }[newMode];
      toast.success(`Mode diganti: ${label}`);
    } catch (e) {
      toast.error("Gagal ganti mode: " + (e?.response?.data?.detail || e.message));
    }
  }
  async function loadConvos() {
    // v84.18 — Filter sidebar percakapan default 2 hari terakhir (owner request).
    // v84.66 — `sidebarDays` state bertambah bila admin klik "Load more" untuk 2 hari lagi.
    const list = await api.listConvos({ days: sidebarDays });
    setConvos(list);
    if (!currentId && list.length > 0) selectConvo(list[0].id);
  }
  // v84.66 — Handler untuk load 2 hari lebih ke belakang.
  // v84.77 — Tidak lagi persist ke localStorage (default selalu 2 hari saat fresh open).
  async function loadMoreDays() {
    if (loadingMoreDays) return;
    setLoadingMoreDays(true);
    const nextDays = sidebarDays + 2;
    try {
      const list = await api.listConvos({ days: nextDays });
      setConvos(list);
      setSidebarDays(nextDays);
      toast.success(`Menampilkan ${nextDays} hari terakhir (${list.length} percakapan)`);
    } catch (e) {
      toast.error("Gagal load: " + (e?.message || "error"));
    } finally {
      setLoadingMoreDays(false);
    }
  }

  // v84.77 — Reset range ke default 2 hari (dipanggil saat scroll ke paling atas
  // setelah admin sempat load more). Skip kalau memang sudah default.
  const resetToDefaultDaysRef = useRef(false);
  async function resetSidebarToDefault() {
    if (sidebarDays <= 2 || resetToDefaultDaysRef.current) return;
    resetToDefaultDaysRef.current = true;
    try {
      const list = await api.listConvos({ days: 2 });
      setConvos(list);
      setSidebarDays(2);
    } catch (e) {
      // silent fail — user masih bisa klik load-more lagi
    } finally {
      // debounce singkat supaya scroll berkali-kali di top tidak spam request
      setTimeout(() => { resetToDefaultDaysRef.current = false; }, 1500);
    }
  }
  // v54 — Play WA-like notif sound (single-oscillator beep via WebAudio, no asset needed).
  // Baca preferensi dari localStorage biar tidak kena stale-closure di setInterval.
  function playNotifSound() {
    try {
      if (localStorage.getItem("gac.soundOn") === "0") return;
    } catch { /* SSR safety */ }
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const now = ctx.currentTime;
      // 2 short beeps ala WA — 880Hz → 1320Hz
      [{ f: 880, t: 0 }, { f: 1320, t: 0.12 }].forEach(({ f, t }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = f;
        osc.type = "sine";
        gain.gain.setValueAtTime(0.0001, now + t);
        gain.gain.exponentialRampToValueAtTime(0.18, now + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.14);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + t);
        osc.stop(now + t + 0.16);
      });
      setTimeout(() => ctx.close(), 400);
    } catch { /* silent — browser policy might block until user gesture */ }
  }

  function toggleSound() {
    setSoundOn(v => {
      const nv = !v;
      try { localStorage.setItem("gac.soundOn", nv ? "1" : "0"); } catch {}
      return nv;
    });
  }

  function toggleReasoning() {
    setShowReasoning(v => {
      const nv = !v;
      try { localStorage.setItem("gac.showReasoning", nv ? "1" : "0"); } catch {}
      return nv;
    });
  }

  // v60 — Debounce search: 300ms setelah user berhenti ketik → hit API
  // v85.29 — Saat search aktif, muat ulang convos tanpa batas 2 hari (days=0)
  //   supaya intersection dengan hasil pencarian tetap menampilkan percakapan lama.
  //   Saat search kosong, kembalikan daftar ke range `sidebarDays` default.
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchMatchedPhones(null);
      // Reload daftar sidebar ke range default saat user menghapus pencarian
      (async () => {
        try {
          const list = await api.listConvos({ days: sidebarDays });
          setConvos(list);
        } catch (_) { /* silent — tick berikutnya akan sync ulang */ }
      })();
      return;
    }
    const t = setTimeout(async () => {
      try {
        const [res, fullList] = await Promise.all([
          api.searchConvos(q),
          api.listConvos({ days: 0, q }),
        ]);
        setSearchMatchedPhones(new Set(res.matched_phones || []));
        setConvos(fullList);
      } catch (e) {
        console.error("search error", e);
        setSearchMatchedPhones(new Set());
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, sidebarDays]);

  async function selectConvo(id) {
    setCurrentId(id);
    // v83.8 — Reset pagination state
    setLoadingOlder(false);
    prependingRef.current = false;
    const d = await api.convoMsgs(id, { days: CHAT_DAYS });
    setMsgs(d.messages || []);
    setHasMoreOlder(!!d.has_more_older);
    oldestTsRef.current = d.oldest_ts_returned || null;
    setLastWoAddress(d.last_wo_address || null);  // v52
    setRecentWos(d.recent_wos || []);              // v53 — legacy fallback
    setShowWoHistory(false);                        // v53 — tutup popover saat switch conv
    setCustomerHistory(null);                        // v74 — reset before fetch
    if (d.conversation) {
      setCurrentConv(d.conversation);
      setCustomerName(d.conversation.customer_name || "");
      setCustomerPhone(d.conversation.customer_phone || "");
      // v44 — Pre-fill textarea admin dgn ai_draft kalau ada (Semi AI mode)
      if (d.conversation.ai_draft) {
        setText(d.conversation.ai_draft);
      }
      // v74 — Fetch full customer history (multi-alamat + semua WO)
      const ph = d.conversation.customer_phone;
      if (ph) {
        api.customerHistoryGet(ph).then(ch => setCustomerHistory(ch)).catch(() => {});
      }
    }
    setReasoning([]);
    // v43 — Reset unread saat admin buka thread ini
    try { await api.markConvoRead(id); } catch {}
    setConvos(cs => cs.map(c => (
      (c.all_conv_ids || [c.id]).includes(id) ? {...c, unread_count: 0} : c
    )));
  }
  useEffect(() => {
    // v84.77 — Hapus persisted sidebarDays lama (kalau ada dari versi sblm v84.77)
    try { localStorage.removeItem("gac.sidebarDays"); } catch {}
    loadConvos(); loadStatus(); loadQuickReplies();  /* eslint-disable-next-line */
  }, []);

  // Auto-refresh 5-detik: reload convos + msgs current supaya pesan WA masuk
  // realtime tanpa admin harus refresh manual. Pause hanya kalau tab ter-blur user
  // (visibilityState=hidden) — bukan sekedar unfocused window.
  // v85.29 — Saat search aktif, tick juga pakai days=0 agar hasil pencarian lama tetap tampil.
  // v85.33 — Fix stale-closure: effect ini hanya depend ke [currentId], jadi searchQuery
  //   tertangkap basi (= "" saat effect pertama kali di-mount). Pakai ref yg di-sync
  //   di effect yg sama supaya `tick` selalu baca nilai terbaru.
  const searchActiveRef = useRef(false);
  const searchQueryRef = useRef("");
  useEffect(() => {
    const q = searchQuery.trim();
    searchActiveRef.current = !!q;
    searchQueryRef.current = q;
  }, [searchQuery]);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (!alive) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try {
        const q = searchActiveRef.current ? searchQueryRef.current : "";
        const effectiveDays = q ? 0 : sidebarDays;
        const list = await api.listConvos({
          days: effectiveDays,
          ...(q ? { q } : {}),
        });
        if (!alive) return;
        // v54 — Deteksi pesan WA baru: kumpulkan ID inbound terbaru dari `list`.
        // Kalau total unread naik dari state lokal → play sound (1x per tick).
        setConvos(prev => {
          const prevUnread = (prev || []).reduce((s, c) => s + (c.unread_count || 0), 0);
          const newUnread = (list || []).reduce((s, c) => s + (c.unread_count || 0), 0);
          if (soundAckedRef.current && newUnread > prevUnread) {
            playNotifSound();
          }
          soundAckedRef.current = true;
          return list;
        });
        if (currentId) {
          const d = await api.convoMsgs(currentId, { days: CHAT_DAYS });
          if (!alive) return;
          // v46 — Update conversation state & pre-fill AI draft real-time.
          // Kalau ai_draft baru muncul (Semi AI mode) dan textarea kosong /
          // masih berisi draft lama, auto-fill supaya admin tidak perlu klik ulang.
          if (d.conversation) {
            setCurrentConv(prevConv => {
              const newDraft = d.conversation.ai_draft;
              const prevDraft = prevConv?.ai_draft;
              if (newDraft && newDraft !== prevDraft) {
                setText(currentText => {
                  // Hanya replace kalau kosong atau berisi draft lama (belum diedit admin)
                  if (!currentText.trim() || currentText === prevDraft) return newDraft;
                  return currentText;
                });
              }
              return d.conversation;
            });
          }
          // v83.8 — Merge: keep msgs older than window, replace/append within-window
          setMsgs(prev => {
            const newMsgs = d.messages || [];
            const cutoffIso = new Date(Date.now() - CHAT_DAYS * 86400000).toISOString();
            const older = prev.filter(m => m.ts && m.ts < cutoffIso);
            const olderIds = new Set(older.map(m => m.id));
            const merged = [...older, ...newMsgs.filter(m => !olderIds.has(m.id))];
            // Skip re-render kalau length + last id sama
            if (merged.length === prev.length &&
                merged[merged.length-1]?.id === prev[prev.length-1]?.id) return prev;
            return merged;
          });
          // v83.8 — Update hasMoreOlder tapi jangan overwrite oldestTsRef
          // (client sudah track cursor lokal dari load-older berikutnya).
          setHasMoreOlder(h => h || !!d.has_more_older);
          // v52 — Refresh alamat WO terakhir kalau berubah
          setLastWoAddress(prev => (d.last_wo_address ?? null) === prev ? prev : (d.last_wo_address ?? null));
          // v53 — Refresh riwayat WO
          setRecentWos(d.recent_wos || []);
        }
      } catch { /* silent — jgn spam toast */ }
    };
    const id = setInterval(tick, 15000);
    // Realtime: pesan WA baru (SSE dari server) → refresh daftar kontak + chat terbuka
    let rtT = null;
    const onMsgNew = () => { clearTimeout(rtT); rtT = setTimeout(tick, 300); };
    window.addEventListener("gac:msg-new", onMsgNew);
    return () => { alive = false; clearInterval(id); clearTimeout(rtT); window.removeEventListener("gac:msg-new", onMsgNew); };
    // eslint-disable-next-line
  }, [currentId]);

  // Refresh badge mode ketika tab kembali dapat fokus (mis. user habis ubah di halaman Pengaturan)
  useEffect(() => {
    const onFocus = () => loadStatus();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line
  }, []);
  // v83.8 — Auto-scroll to bottom hanya kalau bukan sedang prepend older batch
  useEffect(() => {
    if (prependingRef.current) {
      prependingRef.current = false;
      return;
    }
    bottomRef.current?.scrollIntoView({behavior:"smooth"});
  }, [msgs]);
  useEffect(() => { termRef.current?.scrollTo({top: termRef.current.scrollHeight, behavior:"smooth"}); }, [reasoning]);

  // v83.8 — Merge helper: replace/append within-window msgs, keep older loaded batches
  const mergeIntoMsgs = (newMsgs) => {
    setMsgs(prev => {
      const cutoffIso = new Date(Date.now() - CHAT_DAYS * 86400000).toISOString();
      const older = prev.filter(m => m.ts && m.ts < cutoffIso);
      const olderIds = new Set(older.map(m => m.id));
      return [...older, ...(newMsgs || []).filter(m => !olderIds.has(m.id))];
    });
  };

  // v83.8 — Load batch pesan yg lebih tua (dipicu saat scroll ke atas).
  // Preserve scroll position supaya viewport tidak melompat setelah prepend.
  const loadOlder = async () => {
    if (!currentId || loadingOlder || !hasMoreOlder || !oldestTsRef.current) return;
    setLoadingOlder(true);
    const el = chatScrollRef.current;
    const oldScrollHeight = el?.scrollHeight || 0;
    const oldScrollTop = el?.scrollTop || 0;
    try {
      const d = await api.convoMsgs(currentId, {
        days: CHAT_DAYS, before: oldestTsRef.current,
      });
      const newMsgs = d.messages || [];
      if (!newMsgs.length && !d.has_more_older) {
        setHasMoreOlder(false);
        return;
      }
      prependingRef.current = true;   // skip auto-scroll-to-bottom sekali
      setMsgs(prev => {
        const prevIds = new Set(prev.map(m => m.id));
        const filtered = newMsgs.filter(m => !prevIds.has(m.id));
        return [...filtered, ...prev];
      });
      setHasMoreOlder(!!d.has_more_older);
      if (d.oldest_ts_returned) oldestTsRef.current = d.oldest_ts_returned;
      // Restore scroll position setelah DOM update
      requestAnimationFrame(() => {
        if (!el) return;
        const delta = el.scrollHeight - oldScrollHeight;
        el.scrollTop = oldScrollTop + delta;
      });
    } catch (e) {
      console.error("loadOlder error", e);
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleChatScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop <= 60 && hasMoreOlder && !loadingOlder) {
      loadOlder();
    }
  };

  async function toggleGlobalAI(v) {
    await api.aiToggle(v);
    setAiEnabled(v);
    toast[v?"success":"info"](v?"AI Agent AKTIF":"AI Agent NON-AKTIF — admin ambil alih");
  }
  async function toggleConvoAI(v) {
    if (!currentId) return;
    await api.toggleConvoAI(currentId, v);
    setCurrentConv(c => ({...c, ai_paused: v}));
    toast[v?"info":"success"](v?"AI di-pause untuk chat ini":"AI aktif kembali di chat ini");
  }

  // v40 — Kirim template Meta ke customer
  async function sendTemplate() {
    if (!currentId) { toast.error("Pilih percakapan dulu"); return; }
    const name = tplForm.name.trim();
    if (!name) { toast.error("Template ID/Nama wajib diisi"); return; }
    let vars = {};
    if (tplForm.variables.trim()) {
      try {
        vars = JSON.parse(tplForm.variables);
        if (typeof vars !== "object" || Array.isArray(vars)) throw new Error("harus object");
      } catch {
        toast.error("Variables harus JSON valid, mis. {\"nama\":\"Kak\"}");
        return;
      }
    }
    setTplSending(true);
    try {
      const r = await api.sendTemplate(currentId, {
        template_name: name, language: tplForm.language || "id", variables: vars,
      });
      toast.success(r.simulated ? `Template "${name}" (simulasi)` : `Template "${name}" terkirim`);
      setShowTemplate(false);
      setTplForm({ name: "", language: "id", variables: "" });
      // Refresh msgs
      const d = await api.convoMsgs(currentId, { days: CHAT_DAYS });
      mergeIntoMsgs(d.messages);
    } catch (e) {
      toast.error("Gagal kirim: " + (e?.response?.data?.detail || e.message));
    } finally { setTplSending(false); }
  }

  async function newChat() {
    const c = await api.newConvo(customerPhone || "+62-SIM-"+Date.now(), customerName || "Customer");
    await loadConvos();
    selectConvo(c.id);
    setReasoning([{t: new Date().toLocaleTimeString(), line: `[NEW] Percakapan baru dibuat untuk ${c.customer_name}`}]);
  }

  async function send(msgText) {
    if (!msgText?.trim()) return;
    setSending(true);
    setText("");

    if (replyMode === "admin") {
      // Manual admin reply — optimistic add + save
      setMsgs(m => [...m, {id:"tmp-"+Date.now(), direction:"out", text: msgText, from_ai:false, ts:new Date().toISOString(), sender:"admin"}]);
      try {
        await api.manualReply(currentId, msgText);
        const d = await api.convoMsgs(currentId, { days: CHAT_DAYS });
        mergeIntoMsgs(d.messages);
        setReasoning(rs => [...rs, {t:new Date().toLocaleTimeString(), line:`[ADMIN] Balasan manual dikirim`}]);
      } catch (e) { toast.error("Gagal kirim balasan admin"); }
      finally { setSending(false); }
      return;
    }

    // Simulate incoming customer message
    const body = { conversation_id: currentId, customer_name: customerName, customer_phone: customerPhone, text: msgText };
    setMsgs(m => [...m, {id: "tmp-"+Date.now(), direction:"in", text: body.text, ts: new Date().toISOString()}]);
    try {
      const r = await api.simSend(body);
      if (!currentId) setCurrentId(r.conversation_id);
      const d = await api.convoMsgs(r.conversation_id, { days: CHAT_DAYS });
      mergeIntoMsgs(d.messages);
      const evLines = [];
      evLines.push({t: new Date().toLocaleTimeString(), line: `[AI] ${r.reasoning}`});
      (r.events || []).forEach(e => {
        (e.reasoning || "").split("\n").filter(Boolean).forEach(l =>
          evLines.push({t: new Date().toLocaleTimeString(), line: l})
        );
      });
      if (r.ai_off) {
        evLines.push({t:new Date().toLocaleTimeString(), line: `[AI] Nonaktif — pesan tersimpan, menunggu admin balas manual`});
        toast.info("Pesan customer masuk, AI nonaktif. Balas manual.");
      }
      if (r.scheduling) {
        evLines.push({t: new Date().toLocaleTimeString(),
          line: `[SCHEDULED] ${r.scheduling.technician_name} · ${r.scheduling.scheduled_start.slice(0,16).replace('T',' ')}${r.scheduling.distance_km!=null ? ` · ${r.scheduling.distance_km.toFixed(1)}km` : ''}`});
        toast.success(`Terjadwal: ${r.scheduling.technician_name}`);
      }
      setReasoning(rs => [...rs, ...evLines]);
      loadConvos();
    } catch (e) {
      toast.error("Gagal kirim pesan");
    } finally { setSending(false); }
  }

  return (
    <div className="flex flex-col gap-3 h-[calc(100vh-8rem)]" data-testid="simulator-page">
      {/* v44 — 3-mode selector: Manual / Semi AI / Away */}
      {(() => {
        const MODES = [
          { key: "manual", label: "Manual", desc: "Admin balas semua chat sendiri — AI tidak aktif",
            color: "orange", icon: PowerOff },
          { key: "semi",   label: "Semi AI", desc: "AI susun draf balasan, admin edit & kirim manual",
            color: "blue", icon: Headphones },
          { key: "away",   label: "Away", desc: "AI aktif penuh — kirim otomatis ke customer",
            color: "emerald", icon: Power },
        ];
        const active = MODES.find(m => m.key === aiMode) || MODES[0];
        const bg = { orange: "border-orange-300 bg-orange-50", blue: "border-blue-300 bg-blue-50",
                     emerald: "border-emerald-200 bg-emerald-50" }[active.color];
        const textCol = { orange: "text-orange-800", blue: "text-blue-800", emerald: "text-emerald-800" }[active.color];
        const iconCol = { orange: "text-orange-600", blue: "text-blue-600", emerald: "text-emerald-600" }[active.color];
        const Icon = active.icon;
        return (
          <div className={`border rounded-sm px-4 py-2.5 flex items-center gap-3 ${bg}`} data-testid="ai-mode-bar">
            <Icon className={`w-4 h-4 ${iconCol}`} />
            <div className="flex-1 min-w-0">
              <div className={`text-[13px] font-semibold ${textCol}`}>
                Mode AI: <span data-testid="ai-mode-label">{active.label.toUpperCase()}</span>
              </div>
              <div className="text-[11px] text-zinc-600 truncate">{active.desc}</div>
            </div>
            <div className="flex items-center border border-border rounded-sm overflow-hidden shrink-0" role="tablist">
              {MODES.map(m => (
                <button
                  key={m.key}
                  onClick={() => setMode(m.key)}
                  data-testid={`ai-mode-${m.key}`}
                  className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest transition-colors ${
                    aiMode === m.key
                      ? { manual: "bg-orange-600 text-white", semi: "bg-blue-700 text-white", away: "bg-emerald-600 text-white" }[m.key]
                      : "bg-white hover:bg-zinc-50 text-zinc-700"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      <div className={`grid gap-4 flex-1 min-h-0 ${showReasoning ? "grid-cols-[320px_1fr_1fr]" : "grid-cols-[320px_1fr]"}`}>
      {/* Conversations list */}
      <div className="border border-border bg-white rounded-sm flex flex-col overflow-hidden">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">Percakapan</div>
          <Button size="icon" variant="ghost" onClick={newChat} data-testid="new-chat-btn" className="h-7 w-7 rounded-sm">
            <MessageSquarePlus className="w-4 h-4" />
          </Button>
        </div>
        {/* v60 — Kolom pencarian */}
        <div className="px-2 pt-2">
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Cari nama, HP, atau isi chat…"
            className="h-8 rounded-sm text-[12px]"
            data-testid="convo-search-input"
          />
          {searchMatchedPhones && (
            <div className="text-[10px] text-zinc-500 mt-1 px-1" data-testid="convo-search-count">
              {searchMatchedPhones.size} kontak match
            </div>
          )}
          {/* v84.67 — Sidebar filter chips: quick filter by status. Test-ids fixed. */}
          <div className="flex flex-wrap gap-1 mt-2" data-testid="convo-filter-chips">
            {(() => {
              // Hitung count per filter (setelah search filter)
              const base = convos.filter(c => !searchMatchedPhones || searchMatchedPhones.has(c.customer_phone));
              const counts = {
                all: base.length,
                needs_admin: base.filter(c => c.needs_admin).length,
                // v85.40 — Terjadwal menang atas Done: WO aktif (SCHEDULED/has_scheduled_wo)
                //   tetap dihitung Terjadwal walau customer juga punya WO lain yg DONE.
                scheduled: base.filter(c => (c.has_scheduled_wo || (c.statuses || []).includes("SCHEDULED"))).length,
                done: base.filter(c => (c.statuses || []).includes("DONE") && !(c.has_scheduled_wo || (c.statuses || []).includes("SCHEDULED"))).length,
                open: base.filter(c => (c.statuses || []).includes("OPEN") && !(c.statuses || []).includes("DONE") && !(c.statuses || []).includes("SCHEDULED") && !c.has_scheduled_wo).length,
                on24h: base.filter(c => c.within_24h).length,
                unread: base.filter(c => (c.unread_count || 0) > 0).length,
              };
              const chips = [
                { key: "all",         label: "Semua",       cnt: counts.all,         color: "bg-zinc-700 text-white",    activeColor: "bg-white text-zinc-900" },
                { key: "needs_admin", label: "Butuh Admin", cnt: counts.needs_admin, color: "bg-red-950/50 text-red-300 border border-red-800/50", activeColor: "bg-red-600 text-white" },
                { key: "scheduled",   label: "Terjadwal",   cnt: counts.scheduled,   color: "bg-blue-950/50 text-blue-300 border border-blue-800/50", activeColor: "bg-blue-600 text-white" },
                { key: "done",        label: "Done",        cnt: counts.done,        color: "bg-orange-950/50 text-orange-300 border border-orange-800/50", activeColor: "bg-orange-600 text-white" },
                { key: "24h",         label: "24H ON",      cnt: counts.on24h,       color: "bg-emerald-950/50 text-emerald-300 border border-emerald-800/50", activeColor: "bg-emerald-600 text-white" },
                { key: "unread",      label: "Unread",      cnt: counts.unread,      color: "bg-fuchsia-950/50 text-fuchsia-300 border border-fuchsia-800/50", activeColor: "bg-fuchsia-600 text-white" },
              ];
              return chips.map(chip => {
                const active = statusFilter === chip.key;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => {
                      setStatusFilter(chip.key);
                      try { localStorage.setItem("gac.statusFilter", chip.key); } catch {}
                    }}
                    data-testid={`filter-chip-${chip.key}`}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide transition ${active ? chip.activeColor : chip.color} ${active ? "shadow-md" : "hover:bg-zinc-700/60"}`}
                    title={`${chip.label} (${chip.cnt})`}
                  >
                    {chip.label}
                    <span className={`ml-1 opacity-80 font-mono ${active ? "" : "text-[9px]"}`}>{chip.cnt}</span>
                  </button>
                );
              });
            })()}
          </div>
        </div>
        <div
          className="p-2 space-y-1.5 overflow-auto"
          ref={sidebarScrollRef}
          onScroll={(e) => {
            // v84.77 — Kalau admin scroll balik ke paling atas & range > default (2),
            // reset otomatis ke 2 hari. Threshold 4px supaya toleran ke rounding.
            if (sidebarDays > 2 && e.currentTarget.scrollTop <= 4) {
              resetSidebarToDefault();
            }
          }}
          data-testid="sidebar-convos-scroll">
          <div className="px-2 py-1">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Simulasi sebagai</label>
            <Input value={customerName} onChange={e=>setCustomerName(e.target.value)} placeholder="Nama" className="h-8 rounded-sm mt-1" data-testid="sim-name-input" />
            <Input value={customerPhone} onChange={e=>setCustomerPhone(e.target.value)} placeholder="Nomor HP" className="h-8 rounded-sm mt-1" data-testid="sim-phone-input" />
          </div>
          <div className="border-t border-border pt-2 mt-2 space-y-1">
            {convos
              .filter(c => !searchMatchedPhones || searchMatchedPhones.has(c.customer_phone))
              .filter(c => {
                // v84.67 — Status chip filter
                if (statusFilter === "all") return true;
                if (statusFilter === "needs_admin") return !!c.needs_admin;
                if (statusFilter === "24h") return !!c.within_24h;
                if (statusFilter === "unread") return (c.unread_count || 0) > 0;
                const stat = c.statuses || [];
                // v85.40 — Terjadwal menang atas Done.
                if (statusFilter === "scheduled") {
                  return (c.has_scheduled_wo || stat.includes("SCHEDULED"));
                }
                if (statusFilter === "done") {
                  return stat.includes("DONE") && !(c.has_scheduled_wo || stat.includes("SCHEDULED"));
                }
                if (statusFilter === "open") {
                  return stat.includes("OPEN") && !stat.includes("DONE") && !stat.includes("SCHEDULED") && !c.has_scheduled_wo;
                }
                return true;
              })
              // v84.123 — Sort by unread_count DESC saat filter="unread" aktif
              .sort((a, b) => {
                if (statusFilter === "unread") {
                  return (b.unread_count || 0) - (a.unread_count || 0);
                }
                return 0;
              })
              .map(c => {
              const convCount = (c.all_conv_ids || [c.id]).length;
              const stat = (c.statuses || [c.status]).filter(Boolean);
              // v85.40 — Terjadwal menang atas Done. `has_scheduled_wo` otoritatif
              //   (langsung dari work_orders.status=="SCHEDULED") → sinyal utama
              //   WO aktif. Customer dgn WO Done lama + WO akan datang = Terjadwal.
              const isScheduled = c.has_scheduled_wo || stat.includes("SCHEDULED");
              const isDone = !isScheduled && stat.includes("DONE");
              const needsAdmin = !!c.needs_admin;                    // v64
              const unread = c.unread_count || 0;
              // v85.32 — Entry sintetik dari customer_history (belum ada conv doc)
              const isHistoryOnly = !!c.from_history;
              const rowKey = c.id || `hist-${c.customer_phone}`;
              const handleClick = async () => {
                if (isHistoryOnly) {
                  // Buat conversation baru dulu, lalu buka
                  try {
                    const nc = await api.newConvo(c.customer_phone, c.customer_name || "");
                    if (nc?.id) {
                      await selectConvo(nc.id);
                      // Refresh list supaya entry sintetik hilang & conv baru masuk
                      try {
                        const q = searchQuery.trim();
                        const list = await api.listConvos({
                          days: q ? 0 : sidebarDays,
                          ...(q ? { q } : {}),
                        });
                        setConvos(list);
                      } catch (_) {}
                    }
                  } catch (e) {
                    toast.error("Gagal buka pelanggan historis: " + (e?.response?.data?.detail || e.message));
                  }
                  return;
                }
                selectConvo(c.id);
              };
              return (
                <button key={rowKey}
                  data-testid={`convo-${c.id || c.customer_phone}`}
                  onClick={handleClick}
                  className={`w-full text-left px-2.5 py-2 rounded-sm text-[13px] transition-colors ${
                    currentId===c.id ? "bg-[#E65100]/15 border border-[#E65100]/40"
                    : "hover:bg-white/5 border border-transparent"
                  }`}>
                  <div className="flex items-center gap-1.5">
                    <div className={`truncate flex-1 flex items-center gap-1 ${unread > 0 ? "font-bold" : "font-semibold"}`}>
                      {isHistoryOnly && (
                        <span
                          className="shrink-0 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-[1px] rounded font-bold uppercase tracking-wider bg-amber-600/80 text-white"
                          title="Pelanggan historis — belum ada chat. Klik utk mulai percakapan."
                          data-testid={`dot-history-${c.customer_phone}`}
                        >
                          Historis
                        </span>
                      )}
                      {needsAdmin && (
                        <span
                          className="shrink-0 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-[1px] rounded font-bold uppercase tracking-wider bg-red-600 text-white animate-pulse"
                          data-testid={`dot-needs-admin-${c.id}`}
                          title={c.needs_admin_reason || "Butuh intervensi admin"}
                        >
                          <AlertCircle className="w-2.5 h-2.5" />
                          Admin
                        </span>
                      )}
                      <span className="truncate">{c.customer_name || c.customer_phone}</span>
                    </div>
                    {/* v43 — Badge angka pesan belum dibaca */}
                    {unread > 0 && (
                      <span
                        className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center"
                        data-testid={`unread-${c.id}`}
                      >
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                    {/* v84.40 — Badge 24h window — style pill flat seperti Admin badge */}
                    <span
                      className={`shrink-0 text-[9px] px-1.5 py-[1px] rounded font-bold uppercase tracking-wider text-white ${
                        c.within_24h ? "bg-emerald-600" : "bg-red-600"
                      }`}
                      title={c.within_24h ? "Window 24 jam OPEN — bisa balas teks bebas" : "Window CLOSED (>24h) — perlu template Meta"}
                      data-testid={`window-${c.id}`}
                    >
                      {c.within_24h ? "24H ON" : "24H OFF"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-1">
                    <div className="text-[11px] text-zinc-500 truncate flex-1">{c.customer_phone}</div>
                    {/* v84.41 + v84.65 — Timestamp chat terakhir (backend field: `ts`).
                        Format: HH:MM kalau hari ini, "kmrn" kalau kemarin, DD/MM lainnya. */}
                    {c.last_message?.ts && (
                      <div className="text-[10px] font-mono text-zinc-500 shrink-0"
                           data-testid={`convo-time-${c.id}`}>
                        {(() => {
                          const d = new Date(c.last_message.ts);
                          const today = new Date();
                          const isToday = d.toDateString() === today.toDateString();
                          if (isToday) return d.toLocaleTimeString('id-ID', {hour:'2-digit', minute:'2-digit', hour12:false});
                          const yest = new Date(today); yest.setDate(today.getDate()-1);
                          if (d.toDateString() === yest.toDateString()) return "kmrn";
                          return d.toLocaleDateString('id-ID', {day:'2-digit', month:'2-digit'});
                        })()}
                      </div>
                    )}
                  </div>
                  {/* v60 — Preview pesan terakhir (WA-style) */}
                  {c.last_message && (
                    <div
                      className={`text-[12px] mt-0.5 truncate ${
                        unread > 0 ? "text-zinc-300 font-medium" : "text-zinc-500"
                      }`}
                      data-testid={`convo-preview-${c.id}`}
                      title={c.last_message.text}
                    >
                      {c.last_message.direction === "out" && <span className="text-zinc-400">↗ </span>}
                      {c.last_message.text}
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    {isDone && (
                      <Badge className="text-[9px] rounded px-1.5 py-0 bg-orange-600 text-white font-bold uppercase tracking-wider hover:bg-orange-600" data-testid={`badge-done-${c.id}`}>
                        Done
                      </Badge>
                    )}
                    {isScheduled && (
                      <Badge className="text-[9px] rounded px-1.5 py-0 bg-blue-600 text-white font-bold uppercase tracking-wider hover:bg-blue-600" data-testid={`badge-scheduled-${c.id}`}>
                        Terjadwal
                      </Badge>
                    )}
                    {convCount > 1 && (
                      <span className="text-[10px] font-mono text-zinc-400" title={`${convCount} thread WO digabung`}>
                        · {convCount} riwayat
                      </span>
                    )}
                    {c.ai_paused && (
                      <Badge className="text-[9px] rounded-sm px-1.5 py-0 bg-orange-100 text-orange-800 uppercase">
                        AI Pause
                      </Badge>
                    )}
                    {/* v84.65 — Timestamp update terakhir kartu (posisi kanan bawah, sejajar badge status).
                        Update dari updated_at atau last_message.ts (mana yg paling recent). */}
                    {(c.updated_at || c.last_message?.ts) && (
                      <span className="ml-auto text-[10px] font-mono text-zinc-500 shrink-0"
                            data-testid={`convo-time-bottom-${c.id}`}
                            title="Update terakhir">
                        {(() => {
                          const raw = (c.updated_at || "") > (c.last_message?.ts || "")
                            ? c.updated_at : c.last_message?.ts;
                          const d = new Date(raw);
                          const today = new Date();
                          const sameDay = d.toDateString() === today.toDateString();
                          if (sameDay) return d.toLocaleTimeString('id-ID', {hour:'2-digit', minute:'2-digit', hour12:false});
                          const yest = new Date(today); yest.setDate(today.getDate()-1);
                          if (d.toDateString() === yest.toDateString()) return "kmrn";
                          const diffDays = Math.floor((today - d) / (1000*60*60*24));
                          if (diffDays < 7) return `${diffDays}h lalu`;
                          return d.toLocaleDateString('id-ID', {day:'2-digit', month:'2-digit'});
                        })()}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
            {!convos.length && <div className="text-[11px] text-zinc-500 px-2 py-1">Belum ada percakapan. Klik + untuk mulai.</div>}
            {/* v84.66 — Load more: tampilkan 2 hari lebih ke belakang tiap klik. */}
            {convos.length > 0 && (
              <button
                type="button"
                onClick={loadMoreDays}
                disabled={loadingMoreDays}
                data-testid="btn-load-more-days"
                className="mt-2 mx-2 mb-1 flex items-center justify-center gap-2 w-[calc(100%-1rem)] py-2 rounded-md border border-border bg-muted/50 hover:bg-muted text-[11px] font-medium text-zinc-300 hover:text-zinc-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                title={`Tampilkan ${sidebarDays + 2} hari terakhir`}
              >
                {loadingMoreDays ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/>
                      <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                    </svg>
                    Loading…
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                    Load 2 hari lagi ({sidebarDays} → {sidebarDays + 2} hari)
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Chat panel (WA style) */}
      <div className="border border-border bg-[#0B141A] rounded-sm flex flex-col overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border bg-[#075E54] text-white flex items-center gap-2 relative">
          <div className="w-8 h-8 rounded-full bg-white/20 grid place-items-center text-white text-sm font-semibold">
            {(customerName||"?").slice(0,1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold truncate" data-testid="chat-header-name">
              {customerName || "Customer"}
              {lastWoAddress && (
                <span className="font-normal opacity-80" data-testid="chat-header-last-address">
                  {` (${lastWoAddress.length > 40 ? lastWoAddress.slice(0, 40) + "…" : lastWoAddress})`}
                </span>
              )}
            </div>
            <div className="text-[11px] opacity-80 truncate flex items-center gap-1.5">
              <span data-testid="chat-header-phone">{customerPhone}</span>
              {customerPhone && (
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await navigator.clipboard.writeText(customerPhone);
                      toast.success("Nomor disalin", { duration: 1500 });
                    } catch {
                      // Fallback utk browser lama / non-HTTPS
                      const ta = document.createElement("textarea");
                      ta.value = customerPhone;
                      document.body.appendChild(ta);
                      ta.select();
                      try { document.execCommand("copy"); toast.success("Nomor disalin", { duration: 1500 }); }
                      catch { toast.error("Gagal menyalin"); }
                      document.body.removeChild(ta);
                    }
                  }}
                  className="p-0.5 rounded-sm hover:bg-white/20 transition-colors"
                  title="Salin nomor ke clipboard"
                  data-testid="copy-phone-btn"
                >
                  <Copy className="w-3 h-3" />
                </button>
              )}
              {/* v85.1 — Manual send Quick Order CTA ke nomor customer aktif */}
              {customerPhone && currentId && (
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(
                      `Kirim CTA Quick Order ke ${customerPhone} sekarang?\n\n` +
                      `AI akan di-pause 72 jam untuk percakapan ini.`
                    )) return;
                    try {
                      const r = await api.sendForceQoManual(currentId);
                      if (r?.ok) {
                        toast.success(
                          r.simulated ? "CTA terkirim (Simulator)" : "CTA terkirim ke customer",
                          { duration: 2500 }
                        );
                        // Reload messages supaya CTA muncul di chat
                        try { await selectConvo(currentId); } catch {}
                      } else {
                        toast.error(r?.error || "Gagal kirim CTA");
                      }
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || err?.message || "Gagal kirim CTA");
                    }
                  }}
                  className="ml-1 px-1.5 py-0.5 rounded-sm bg-amber-500/25 hover:bg-amber-500/40 text-amber-100 flex items-center gap-1 transition-colors"
                  title="Kirim manual CTA Quick Order ke nomor ini (AI pause 72h)"
                  data-testid="send-manual-fqo-btn"
                >
                  <Zap className="w-3 h-3" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider">Kirim CTA</span>
                </button>
              )}
            </div>
          </div>
          {/* v74 — Badge "N WO" + popover riwayat lengkap dari customer_history collection.
              Fallback ke recent_wos legacy kalau customer_history belum ada. */}
          {((customerHistory?.work_orders?.length ?? 0) > 0 || recentWos.length > 0) && currentId && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowWoHistory(v => !v)}
                className="flex items-center gap-1 px-2 py-1 rounded-sm bg-white/15 hover:bg-white/25 text-[10px] font-semibold uppercase tracking-wider transition-colors"
                title="Lihat riwayat lengkap customer"
                data-testid="wo-history-button"
              >
                <History className="w-3 h-3" />
                <span data-testid="wo-history-count">
                  {customerHistory?.work_orders?.length ?? recentWos.length} WO
                </span>
              </button>
              {showWoHistory && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowWoHistory(false)}
                    data-testid="wo-history-backdrop" />
                  <div className="absolute right-0 top-full mt-1 w-96 bg-white text-zinc-900 rounded-sm shadow-lg border border-zinc-200 z-20 overflow-hidden"
                    data-testid="wo-history-popover">
                    <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[11px] font-semibold uppercase tracking-wider text-zinc-600 flex items-center justify-between">
                      <span>Riwayat Customer</span>
                      {customerHistory?.last_updated && (
                        <span className="text-[9px] normal-case font-normal text-zinc-400">
                          upd. {customerHistory.last_updated.slice(0, 10)}
                        </span>
                      )}
                    </div>
                    {/* v74 — Multi-alamat section */}
                    {customerHistory?.addresses?.length > 0 && (
                      <div className="px-3 py-2 border-b border-zinc-200 bg-blue-50/40" data-testid="wo-history-addresses">
                        <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                          📍 Alamat ({customerHistory.addresses.length})
                        </div>
                        <div className="space-y-0.5">
                          {customerHistory.addresses.map((a, i) => (
                            <div key={i} className="text-[11px] text-zinc-700 truncate" data-testid={`wo-history-addr-${i}`}>
                              • {a}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="max-h-80 overflow-y-auto divide-y divide-zinc-100">
                      {(customerHistory?.work_orders?.length > 0
                        ? [...customerHistory.work_orders].reverse()   // newest first
                        : recentWos
                      ).map((w, i) => {
                        // customer_history uses lowercase status; legacy uses UPPER
                        const st = (w.status || "").toLowerCase();
                        const pillCls =
                          st === "done"        ? "bg-emerald-100 text-emerald-800" :
                          st === "in-progress" ? "bg-amber-100 text-amber-800" :
                          st === "pending"     ? "bg-blue-100 text-blue-800" :
                          st === "cancelled"   ? "bg-red-100 text-red-800" :
                          // legacy UPPER
                          w.status === "DONE"       ? "bg-emerald-100 text-emerald-800" :
                          w.status === "SCHEDULED"  ? "bg-blue-100 text-blue-800" :
                          w.status === "IN_PROGRESS"? "bg-amber-100 text-amber-800" :
                          w.status === "CANCELLED"  ? "bg-red-100 text-red-800" :
                                                       "bg-zinc-100 text-zinc-700";
                        return (
                          <div key={w.wo_number || w.id || i} className="px-3 py-2 hover:bg-zinc-50"
                               data-testid={`wo-history-item-${i}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="text-[12px] font-semibold truncate">
                                  {w.service_type || w.title || "Order"}
                                  {w.quantity > 1 && <span className="text-zinc-500 font-normal"> · {w.quantity} unit</span>}
                                </div>
                                {w.address && (
                                  <div className="text-[10.5px] text-zinc-500 truncate mt-0.5">
                                    📍 {w.address}
                                  </div>
                                )}
                                <div className="text-[10px] text-zinc-400 mt-0.5">
                                  {(w.date || w.scheduled_start || w.created_at || "").slice(0, 10) || "—"}
                                </div>
                              </div>
                              <Badge className={`rounded-sm text-[9px] uppercase tracking-wider shrink-0 ${pillCls}`}>
                                {(w.status || "—").replace("_", " ")}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {currentId && (
            <div className="flex items-center gap-1.5 mr-2" title="Pause AI di chat ini agar admin bisa balas manual">
              <Headphones className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-widest">AI</span>
              <Switch
                checked={!currentConv?.ai_paused}
                onCheckedChange={(v)=>toggleConvoAI(!v)}
                data-testid="convo-ai-toggle"
                className="scale-75"
              />
            </div>
          )}
          <Badge
            className={`rounded-sm text-[10px] font-semibold ${
              waMode === "live"
                ? "bg-emerald-500 text-white"
                : "bg-white/20 text-white"
            }`}
            data-testid="wa-mode-badge"
          >
            {waMode === "live" ? "WA Live" : "WA Simulator"}
          </Badge>
          {/* v54 — Toggle sound notif */}
          <button
            type="button"
            onClick={toggleSound}
            className="ml-1 p-1.5 rounded-sm hover:bg-white/15 transition-colors"
            title={soundOn ? "Notifikasi suara AKTIF (klik untuk mute)" : "Notifikasi suara MUTE (klik untuk aktifkan)"}
            data-testid="toggle-sound-btn"
          >
            {soundOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4 opacity-60" />}
          </button>
          {/* v64 — Tombol Tandai Beres (dismiss needs_admin) */}
          {currentConv?.needs_admin && (
            <button
              type="button"
              onClick={async () => {
                try {
                  await api.markConvoResolved(currentId);
                  // optimistic — hide immediately, sync full via next tick
                  setCurrentConv(prev => prev ? {...prev, needs_admin: false} : prev);
                  setConvos(prev => prev.map(c =>
                    c.customer_phone === currentConv.customer_phone
                      ? {...c, needs_admin: false} : c
                  ));
                } catch (e) { console.error("mark-resolved error", e); }
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-sm bg-red-500/90 hover:bg-red-500 text-white text-[10px] font-semibold transition-colors"
              title="Dismiss notifikasi 'Butuh Admin' tanpa balas chat"
              data-testid="mark-resolved-btn"
            >
              <Check className="w-3 h-3" />
              Tandai Beres
            </button>
          )}
          {/* v54 — Toggle panel AI Reasoning */}
          <button
            type="button"
            onClick={toggleReasoning}
            className="p-1.5 rounded-sm hover:bg-white/15 transition-colors"
            title={showReasoning ? "Sembunyikan panel AI Reasoning" : "Tampilkan panel AI Reasoning"}
            data-testid="toggle-reasoning-btn"
          >
            {showReasoning ? <PanelRightClose className="w-4 h-4" /> : <PanelRight className="w-4 h-4" />}
          </button>
        </div>
        <div ref={chatScrollRef}
             onScroll={handleChatScroll}
             className="flex-1 overflow-auto p-4 space-y-2"
             data-testid="chat-scroll-container">
          {/* v84.86 — Infinite scroll ke atas: loader indicator saat load older batch */}
          {loadingOlder && (
            <div className="text-center py-2 text-[11px] text-zinc-400"
                 data-testid="chat-loading-older">
              Memuat 7 hari sebelumnya…
            </div>
          )}
          {!hasMoreOlder && msgs.length > 0 && (
            <div className="text-center py-2 text-[10px] text-zinc-300 italic"
                 data-testid="chat-history-start">
              — awal riwayat chat —
            </div>
          )}
          {msgs.map(m => (
            <div key={m.id} className={`flex ${m.direction==='out'?'justify-end':'justify-start'}`}>
              <div className="max-w-[80%]">
                <div className={`px-3 py-2 rounded-lg text-[13px] whitespace-pre-wrap fade-in ${
                  m.direction==='out'
                    ? (m.sender === 'system-followup'
                        ? 'wa-out !bg-[#4A2E0F] !border-[#B26A0F] text-amber-100'
                        : ((m.sender === 'system-reminder-v1' || m.sender === 'system-reminder-v2')
                            ? 'wa-out !bg-[#3A1F52] !border-[#7C3AED] text-purple-100'
                            : (m.from_ai === false ? 'wa-out !bg-[#1E3A5F] !border-[#2E5FA8] text-blue-50' : 'wa-out')))
                    : 'wa-in'
                }`} data-testid={`msg-${m.id}`}>
                  {m.direction==='out' && m.sender === 'system-followup' && (
                    <div className="text-[9px] font-semibold uppercase tracking-widest text-amber-300 mb-0.5"
                         data-testid={`followup-label-${m.id}`}>
                      🔔 Follow-up Otomatis
                    </div>
                  )}
                  {m.direction==='out' && (m.sender === 'system-reminder-v1' || m.sender === 'system-reminder-v2') && (
                    <div className="text-[9px] font-semibold uppercase tracking-widest text-purple-300 mb-0.5"
                         data-testid={`reminder-label-${m.id}`}>
                      🔔 Reminder Otomatis {m.sender === 'system-reminder-v2' ? '(H-3)' : ''}
                    </div>
                  )}
                  {m.direction==='out' && m.from_ai === false && m.sender !== 'system-followup' && m.sender !== 'system-reminder-v1' && m.sender !== 'system-reminder-v2' && (
                    <div className="text-[9px] font-semibold uppercase tracking-widest text-blue-300 mb-0.5">
                      Admin
                    </div>
                  )}
                  {/* v45 — Render media inline (image/video/audio/dokumen) */}
                  {m.media_id && (
                    <div className="mb-1.5">
                      <MediaBubble msg={m} apiBase={apiBase} />
                      {m.media_caption && (
                        <div className="text-[11px] italic text-zinc-600 mt-1">{m.media_caption}</div>
                      )}
                    </div>
                  )}
                  {/* v47 — Render lokasi share sbg tautan Google Maps clickable */}
                  {m.location_lat != null && m.location_lng != null && (
                    <a
                      href={`https://maps.google.com/?q=${m.location_lat},${m.location_lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mb-1.5 flex items-center gap-2 px-2.5 py-2 rounded-md bg-emerald-50 border border-emerald-300 hover:bg-emerald-100 transition-colors text-emerald-900 no-underline"
                      data-testid={`location-link-${m.id}`}
                    >
                      <span className="text-lg leading-none">📍</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold truncate">
                          {m.location_name || m.location_address || "Lokasi Dikirim"}
                        </div>
                        <div className="text-[10px] font-mono text-emerald-700">
                          {m.location_lat.toFixed(6)}, {m.location_lng.toFixed(6)} · <span className="underline">Buka di Google Maps</span>
                        </div>
                      </div>
                    </a>
                  )}
                  {m.text && !m.location_lat && m.text}
                  {/* Kalau ini pesan lokasi, sembunyikan text mentah (sudah diwakili link maps) */}
                  {m.location_lat != null && m.location_address && (
                    <div className="text-[11px] text-zinc-600 italic">{m.location_address}</div>
                  )}
                  <div className="text-[9px] text-zinc-500 mt-1 text-right font-mono flex items-center justify-end gap-1">
                    <span data-testid={`msg-ts-${m.id}`}>
                      {(() => {
                        // v55 — Format lengkap: "Aug 20, 10:30 AM"
                        try {
                          const d = new Date(m.ts);
                          return d.toLocaleString("en-US", {
                            month: "short", day: "numeric",
                            hour: "numeric", minute: "2-digit", hour12: true,
                          });
                        } catch { return ""; }
                      })()}
                    </span>
                    {/* v40 — Status tick untuk outbound msg */}
                    {m.direction==='out' && (
                      <span data-testid={`msg-status-${m.id}`} title={m.status || "pending"}>
                        <StatusTick status={m.status} />
                      </span>
                    )}
                  </div>
                </div>
                {/* v43 — Label "Dibalas AI Asisten" utk pesan AI (outbound + from_ai=true) */}
                {m.direction==='out' && m.from_ai !== false && (
                  <div className="text-[9px] text-zinc-400 italic text-right pr-1 pt-0.5" data-testid={`ai-label-${m.id}`}>
                    Dibalas AI Asisten
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && <div className="flex justify-end"><div className="wa-out px-3 py-2 rounded-lg text-[13px] italic text-zinc-500">AI mengetik<span className="blink">…</span></div></div>}
          {!msgs.length && (
            <div className="text-center text-zinc-500 text-[12px] mt-8">
              Belum ada pesan. Coba salah satu:
              <div className="mt-3 space-y-1.5">
                {SUGGESTIONS.map(s => (
                  <button key={s} data-testid={`suggest-${s.slice(0,10)}`} onClick={()=>send(s)}
                    className="block mx-auto text-[12px] px-3 py-1.5 rounded-sm bg-white border border-border hover:border-[#E65100] transition-colors">
                    "{s}"
                  </button>
                ))}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <div className="p-3 border-t border-border bg-white">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex border border-border rounded-sm overflow-hidden text-[11px]">
              <button
                onClick={()=>setReplyMode("customer")}
                className={`px-2.5 py-1 transition-colors ${replyMode==="customer"?"bg-[#25D366] text-white font-semibold":"bg-white hover:bg-zinc-50"}`}
                data-testid="mode-customer">Sebagai Customer</button>
              <button
                onClick={()=>setReplyMode("admin")}
                className={`px-2.5 py-1 transition-colors ${replyMode==="admin"?"bg-[#2962FF] text-white font-semibold":"bg-white hover:bg-zinc-50"}`}
                data-testid="mode-admin">Balas sebagai Admin</button>
            </div>
            <div className="text-[10px] text-zinc-500 flex-1">
              {replyMode==="customer" ? "Simulasi pesan masuk dari customer" : "Kirim balasan manual — AI tidak dipanggil"}
            </div>
            {/* v40 — Template send button */}
            <button
              onClick={()=>setShowTemplate(s=>!s)}
              className={`text-[11px] px-2.5 py-1 rounded-sm border flex items-center gap-1 transition-colors ${
                showTemplate ? "bg-blue-100 border-blue-300 text-blue-800" : "bg-white border-border hover:border-blue-400"
              }`}
              data-testid="template-toggle-btn"
              title="Kirim template Meta (buat re-open window 24h)"
              disabled={!currentId}
            >
              <FileText className="w-3 h-3" /> Template
            </button>
          </div>

          {/* v44 — Semi AI mode: kalau convo punya ai_draft, tampilkan hint biru */}
          {aiMode === "semi" && currentConv?.ai_draft && (
            <div
              className="mb-2 flex items-center gap-2 px-3 py-2 rounded-sm bg-blue-50 border border-blue-300 text-[11px] text-blue-800"
              data-testid="ai-draft-hint"
            >
              <Bot className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <div className="flex-1">
                <b>Draf AI dimuat</b> — edit lalu klik Kirim, atau <button
                  onClick={async () => {
                    try {
                      await api.dismissDraft(currentId);
                      setText("");
                      setCurrentConv(c => c ? {...c, ai_draft: null} : c);
                      toast.info("Draf dibuang");
                    } catch {}
                  }}
                  className="underline font-semibold hover:text-blue-900"
                  data-testid="dismiss-draft-btn"
                >buang draf</button> dan tulis sendiri.
              </div>
            </div>
          )}

          {/* v41 — Auto-guide: kalau admin balas manual di convo yg window 24h CLOSED,
              ingatkan pakai template. Owner tidak perlu hafal aturan Meta. */}
          {replyMode === "admin" && currentId && (() => {
            const cur = convos.find(c => (c.all_conv_ids || [c.id]).includes(currentId));
            if (cur && cur.within_24h === false) {
              return (
                <div
                  className="mb-2 flex items-center gap-2 px-3 py-2 rounded-sm bg-red-50 border border-red-300 text-[11px] text-red-800"
                  data-testid="window-closed-hint"
                >
                  <span className="text-base leading-none">⚠️</span>
                  <div className="flex-1">
                    <b>Window 24 jam sudah CLOSED.</b> Pesan teks biasa dari admin
                    akan ditolak Meta. Pakai <b>tombol Template</b> di kanan agar pesan tetap sampai.
                  </div>
                  <button
                    onClick={() => setShowTemplate(true)}
                    className="text-[11px] px-2 py-1 rounded-sm bg-red-700 hover:bg-red-800 text-white font-semibold shrink-0"
                    data-testid="hint-open-template-btn"
                  >
                    Buka Template
                  </button>
                </div>
              );
            }
            return null;
          })()}

          {/* v40 — Template send form */}
          {showTemplate && (
            <div className="mb-2 border border-blue-200 bg-blue-50/50 rounded-sm p-2.5 space-y-2" data-testid="template-form">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-blue-700 font-bold">
                <FileText className="w-3 h-3" /> Kirim Template Meta
                <button onClick={()=>setShowTemplate(false)} className="ml-auto text-zinc-400 hover:text-zinc-600">
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input
                  placeholder="Template Name (mis. invoice_service_ac_v2)"
                  value={tplForm.name}
                  onChange={e=>setTplForm({...tplForm, name: e.target.value})}
                  className="rounded-sm h-8 text-[12px]"
                  data-testid="template-name-input"
                />
                <Input
                  placeholder="Language (id/en_US)"
                  value={tplForm.language}
                  onChange={e=>setTplForm({...tplForm, language: e.target.value})}
                  className="rounded-sm h-8 text-[12px] font-mono"
                  data-testid="template-lang-input"
                />
                <Input
                  placeholder='Variables JSON, mis. {"nama":"Kak"}'
                  value={tplForm.variables}
                  onChange={e=>setTplForm({...tplForm, variables: e.target.value})}
                  className="rounded-sm h-8 text-[12px] font-mono"
                  data-testid="template-vars-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="text-[10px] text-blue-700 flex-1">
                  💡 Preview: <b>{tplForm.name || "—"}</b> · <span className="font-mono">{tplForm.language}</span>
                  {tplForm.variables && (
                    <span className="ml-1">+ {(() => {
                      try { return Object.keys(JSON.parse(tplForm.variables)).length + " vars"; }
                      catch { return "❌ invalid JSON"; }
                    })()}</span>
                  )}
                </div>
                <Button
                  size="sm" onClick={sendTemplate} disabled={tplSending || !tplForm.name.trim()}
                  className="h-7 rounded-sm gap-1 bg-blue-700 hover:bg-blue-800 text-[11px]"
                  data-testid="template-send-btn"
                >
                  <Send className="w-3 h-3" />
                  {tplSending ? "Mengirim…" : "Kirim Template"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 relative">
            {/* v84.81 — Quick Reply button */}
            <button
              type="button"
              onClick={() => setShowQuickReply(v => !v)}
              className={`self-end mb-0 shrink-0 h-[42px] w-[42px] rounded-sm border flex items-center justify-center transition-colors ${
                showQuickReply ? "bg-orange-100 border-orange-400 text-orange-700" : "bg-white border-border hover:bg-zinc-50 text-zinc-600"
              }`}
              title="Quick Reply — balasan cepat"
              data-testid="quick-reply-btn"
            >
              <Zap className="w-4 h-4" />
            </button>
            {showQuickReply && (
              <div
                className="absolute bottom-[52px] left-0 z-30 bg-white border border-border rounded-md shadow-xl w-[360px] max-h-[380px] flex flex-col"
                data-testid="quick-reply-popup"
              >
                {/* Header: input tambah baru */}
                <div className="p-2 border-b border-zinc-200 bg-zinc-50">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-1.5">
                    Quick Reply
                  </div>
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={newQrText}
                      onChange={e => setNewQrText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSaveQuickReply(); } }}
                      placeholder="Tambah quick reply baru…"
                      className="flex-1 h-8 px-2 text-[12px] border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
                      data-testid="quick-reply-new-input"
                    />
                    <button
                      type="button"
                      onClick={handleSaveQuickReply}
                      disabled={qrSaving || !newQrText.trim()}
                      className="h-8 px-2 bg-orange-500 text-white rounded-sm hover:bg-orange-600 disabled:opacity-40 flex items-center gap-1"
                      data-testid="quick-reply-save-btn"
                    >
                      <Plus className="w-3 h-3" />
                      <span className="text-[11px] font-semibold">Simpan</span>
                    </button>
                  </div>
                </div>
                {/* List — sorted by use_count DESC di backend */}
                <div className="overflow-y-auto flex-1">
                  {quickReplies.length === 0 && (
                    <div className="text-center text-[12px] text-zinc-400 py-6" data-testid="quick-reply-empty">
                      Belum ada quick reply. Tambahkan di atas.
                    </div>
                  )}
                  {quickReplies.map(qr => (
                    <div
                      key={qr.id}
                      className="flex items-start gap-1 px-2 py-1.5 hover:bg-orange-50 border-b border-zinc-100 last:border-b-0 group"
                      data-testid={`quick-reply-item-${qr.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => handlePickQuickReply(qr)}
                        className="flex-1 text-left text-[12px] text-zinc-800 leading-snug"
                        data-testid={`quick-reply-pick-${qr.id}`}
                      >
                        {qr.text}
                        {qr.use_count > 0 && (
                          <span className="ml-1 text-[10px] text-zinc-400">({qr.use_count}×)</span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteQuickReply(qr)}
                        className="opacity-0 group-hover:opacity-100 shrink-0 w-6 h-6 rounded hover:bg-red-100 text-red-500 flex items-center justify-center transition-opacity"
                        title="Hapus quick reply"
                        data-testid={`quick-reply-delete-${qr.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* v45 — Emoji picker button */}
            <button
              type="button"
              onClick={() => setShowEmoji(v => !v)}
              className={`self-end mb-0 shrink-0 h-[42px] w-[42px] rounded-sm border flex items-center justify-center transition-colors ${
                showEmoji ? "bg-yellow-100 border-yellow-400 text-yellow-700" : "bg-white border-border hover:bg-zinc-50 text-zinc-600"
              }`}
              title="Sisipkan emoji"
              data-testid="emoji-toggle-btn"
            >
              <Smile className="w-4 h-4" />
            </button>
            {/* v46 — Attach media button */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleMediaUpload}
              accept="image/*,video/*,audio/*,application/pdf"
              className="hidden"
              data-testid="media-file-input"
            />
            <button
              type="button"
              onClick={() => setShowAttachMenu(v => !v)}
              disabled={uploading || !currentId}
              className={`self-end mb-0 shrink-0 h-[42px] w-[42px] rounded-sm border flex items-center justify-center transition-colors relative ${
                uploading ? "bg-blue-100 border-blue-400 text-blue-700 animate-pulse"
                          : "bg-white border-border hover:bg-zinc-50 text-zinc-600 disabled:opacity-40"
              }`}
              title="Kirim lampiran"
              data-testid="media-attach-btn"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            {showAttachMenu && (
              <div
                className="absolute bottom-[52px] left-[92px] z-30 bg-white border border-border rounded-md shadow-lg p-1 w-[180px]"
                data-testid="attach-menu"
              >
                <button
                  type="button"
                  onClick={() => { setShowAttachMenu(false); fileInputRef.current?.click(); }}
                  className="w-full text-left px-3 py-2 text-[12px] rounded-sm hover:bg-zinc-100 flex items-center gap-2"
                  data-testid="attach-media-btn"
                >
                  <Paperclip className="w-3.5 h-3.5" /> Kirim Media
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAttachMenu(false); setShowContactModal(true); }}
                  className="w-full text-left px-3 py-2 text-[12px] rounded-sm hover:bg-zinc-100 flex items-center gap-2"
                  data-testid="attach-contact-btn"
                >
                  <User className="w-3.5 h-3.5" /> Kirim Kontak
                </button>
              </div>
            )}
            {showEmoji && (
              <div
                className="absolute bottom-[52px] left-0 z-20 bg-white border border-border rounded-md shadow-lg p-2 grid grid-cols-10 gap-0.5 w-[280px] max-h-[220px] overflow-y-auto"
                data-testid="emoji-picker"
              >
                {EMOJI_LIST.map(e => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => {
                      setText(t => t + e);
                      setShowEmoji(false);   // v84.41 — auto-hide setelah pilih emoji
                    }}
                    className="w-6 h-6 text-lg leading-none rounded hover:bg-zinc-100 transition-colors flex items-center justify-center"
                    data-testid={`emoji-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
            <Textarea data-testid="sim-message-input" value={text} onChange={e=>setText(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();send(text);}}}
              onFocus={()=>setShowEmoji(false)}
              rows={4}
              placeholder={replyMode==="admin" ? "Ketik balasan manual ke customer…" : "Ketik pesan sebagai customer…"}
              className="min-h-[96px] max-h-56 rounded-sm resize-none" />
            {/* v83 — Push-to-talk mic (Web Speech API). Tahan tombol utk rekam, lepas utk stop+konversi. */}
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); startMic(); }}
              onMouseUp={(e) => { e.preventDefault(); stopMic(); }}
              onMouseLeave={() => { if (micActiveRef.current) stopMic(); }}
              onTouchStart={(e) => { e.preventDefault(); startMic(); }}
              onTouchEnd={(e) => { e.preventDefault(); stopMic(); }}
              onTouchCancel={() => { if (micActiveRef.current) stopMic(); }}
              onContextMenu={(e) => e.preventDefault()}
              disabled={!micSupported || !currentId}
              className={`self-end mb-0 shrink-0 h-[42px] w-[42px] rounded-sm border flex items-center justify-center transition-colors select-none ${
                micRecording
                  ? "bg-red-600 border-red-700 text-white animate-pulse"
                  : "bg-white border-border hover:bg-zinc-50 text-zinc-600 disabled:opacity-40"
              }`}
              title={micSupported ? "Tahan untuk merekam (lepas untuk konversi ke teks)" : "Browser tidak mendukung Web Speech API"}
              data-testid="mic-push-to-talk-btn"
            >
              <Mic className="w-4 h-4" />
            </button>
            <Button data-testid="sim-send-btn" onClick={()=>send(text)} disabled={sending}
                    className={`${replyMode==="admin"?"bg-[#2962FF] hover:bg-[#2979FF]":"bg-[#25D366] hover:bg-[#128C7E]"} rounded-sm`}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* AI reasoning terminal — v54 collapsible */}
      {showReasoning && (
      <div className="border border-border rounded-sm flex flex-col overflow-hidden terminal" data-testid="reasoning-panel">
        <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-2 bg-black">
          <Terminal className="w-3.5 h-3.5 text-[#4ADE80]" />
          <div className="text-[11px] uppercase tracking-widest font-semibold">AI Agent Reasoning · Claude 4.5</div>
          <div className="ml-auto text-[10px] text-zinc-500 font-mono">{reasoning.length} events</div>
        </div>
        <div ref={termRef} className="flex-1 overflow-auto p-3 space-y-1">
          {reasoning.length === 0 && <div className="text-zinc-500">// menunggu pesan customer…<span className="blink">_</span></div>}
          {reasoning.map((r, i) => (
            <div key={i} className="fade-in leading-relaxed">
              <span className="text-zinc-500">[{r.t}]</span> <span className="text-[#4ADE80]">{r.line}</span>
            </div>
          ))}
        </div>
      </div>
      )}
      </div>

      {/* v84.142 — Contact/vCard send modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="contact-modal">
          <div className="bg-white rounded-md shadow-xl w-full max-w-sm p-4">
            <div className="text-sm font-semibold text-zinc-800 mb-3">Kirim Kontak ke Customer</div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Nama</label>
            <input
              type="text"
              value={contactForm.name}
              onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Nama kontak"
              className="w-full mb-3 rounded-sm border border-zinc-300 px-2 py-1.5 text-sm"
              data-testid="contact-name-input"
            />
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Nomor HP</label>
            <input
              type="tel"
              value={contactForm.phone}
              onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="0812xxx / 6281xxx"
              className="w-full mb-4 rounded-sm border border-zinc-300 px-2 py-1.5 text-sm font-mono"
              data-testid="contact-phone-input"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowContactModal(false); setContactForm({ name: "", phone: "" }); }}
                className="px-3 py-1.5 text-[12px] rounded-sm border border-zinc-300 hover:bg-zinc-50"
                data-testid="contact-cancel-btn"
              >Batal</button>
              <button
                onClick={async () => {
                  if (!contactForm.name.trim() || !contactForm.phone.trim()) {
                    toast.error("Nama & nomor HP wajib diisi");
                    return;
                  }
                  if (!currentId) return;
                  setSendingContact(true);
                  try {
                    await api.simSendContact(currentId, contactForm.name.trim(), contactForm.phone.trim());
                    toast.success("Kontak terkirim ke customer");
                    setShowContactModal(false);
                    setContactForm({ name: "", phone: "" });
                    // Poll akan refresh messages otomatis dalam 5 detik
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Gagal kirim kontak");
                  } finally {
                    setSendingContact(false);
                  }
                }}
                disabled={sendingContact}
                className="px-3 py-1.5 text-[12px] rounded-sm bg-[#25D366] text-white font-semibold hover:bg-[#128C7E] disabled:opacity-60"
                data-testid="contact-send-btn"
              >{sendingContact ? "Mengirim..." : "Kirim Kontak"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
